import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { LoadingSpinner } from '../LoadingSpinner';
import { formatTimeAgo, formatDuration, formatPlayerTime } from '../../utils/format';
import { Card } from '../ui/Card';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AlbumIcon from '@mui/icons-material/Album';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface ActivityItem {
  id: string;
  type: 'stream' | 'favorite' | 'playlist_add' | 'skip' | 'session_start' | 'session_end';
  timestamp: string;
  song?: {
    id: string;
    title: string;
    artist_name: string;
    album_name?: string;
    duration?: number;
    cover_art?: string;
  };
  playlist?: {
    id: string;
    name: string;
  };
  duration?: number;
  completed?: boolean;
  metadata?: {
    completion_percentage?: number;
    skip_reason?: string;
    session_duration?: number;
  };
}

interface ActivityFeedData {
  activities: ActivityItem[];
  totalCount: number;
  hasMore: boolean;
}

interface ActivityFeedProps {
  limit?: number;
  showPlayButton?: boolean;
  compact?: boolean;
}

export function ActivityFeed({ limit = 20, showPlayButton = false, compact = false }: ActivityFeedProps) {
  const { user } = useAuth();
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'completed' | 'favorites' | 'skipped'>('all');

  const { data: activityData, isLoading, error, refetch } = useQuery<ActivityFeedData>({
    queryKey: ['analytics', 'activity-feed', limit, selectedFilter],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/analytics/activity-feed?limit=${limit}&filter=${selectedFilter}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch activity feed');
      }
      
      return response.json();
    },
    enabled: !!user,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time updates
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !activityData?.activities) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <p className="text-slate-grey mb-4">Unable to load activity feed</p>
          <button
            onClick={() => refetch()}
            className="text-naia-pink hover:text-soft-violet transition-colors"
          >
            Try again
          </button>
        </div>
      </Card>
    );
  }

  const getActivityIcon = (item: ActivityItem) => {
    switch (item.type) {
      case 'stream':
        return item.completed 
          ? <CheckCircleIcon className="text-cyber-teal" />
          : <PlayArrowIcon className="text-electric-blue" />;
      case 'favorite':
        return <FavoriteIcon className="text-naia-pink" />;
      case 'playlist_add':
        return <AlbumIcon className="text-soft-violet" />;
      case 'skip':
        return <SkipNextIcon className="text-slate-grey" />;
      case 'session_start':
      case 'session_end':
        return <AccessTimeIcon className="text-electric-blue" />;
      default:
        return <MusicNoteIcon className="text-pearl-white" />;
    }
  };

  const getActivityText = (item: ActivityItem) => {
    switch (item.type) {
      case 'stream':
        if (item.completed) {
          return (
            <>
              <span className="text-pearl-white">Completed</span> {item.song?.title}
              {item.metadata?.completion_percentage && item.metadata.completion_percentage < 100 && (
                <span className="text-slate-grey text-sm ml-2">
                  ({Math.round(item.metadata.completion_percentage)}%)
                </span>
              )}
            </>
          );
        } else {
          return (
            <>
              <span className="text-pearl-white">Played</span> {item.song?.title}
              {item.duration && (
                <span className="text-slate-grey text-sm ml-2">
                  for {formatPlayerTime(item.duration)}
                </span>
              )}
            </>
          );
        }
      case 'favorite':
        return (
          <>
            <span className="text-pearl-white">Added to favorites</span> {item.song?.title}
          </>
        );
      case 'playlist_add':
        return (
          <>
            <span className="text-pearl-white">Added</span> {item.song?.title} 
            <span className="text-pearl-white"> to</span> {item.playlist?.name}
          </>
        );
      case 'skip':
        return (
          <>
            <span className="text-pearl-white">Skipped</span> {item.song?.title}
            {item.metadata?.skip_reason && (
              <span className="text-slate-grey text-sm ml-2">
                ({item.metadata.skip_reason})
              </span>
            )}
          </>
        );
      case 'session_start':
        return <span className="text-pearl-white">Started listening session</span>;
      case 'session_end':
        return (
          <>
            <span className="text-pearl-white">Ended listening session</span>
            {item.metadata?.session_duration && (
              <span className="text-slate-grey text-sm ml-2">
                ({formatDuration(item.metadata.session_duration)})
              </span>
            )}
          </>
        );
      default:
        return <span className="text-pearl-white">Unknown activity</span>;
    }
  };

  const handlePlaySong = (songId: string) => {
    // This would integrate with the player context to play the song
    console.log('Play song:', songId);
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-pearl-white">
          Recent Activity
        </h3>
        
        {/* Filter Options */}
        {!compact && (
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'completed', label: 'Completed' },
              { key: 'favorites', label: 'Favorites' }, 
              { key: 'skipped', label: 'Skipped' }
            ].map(filter => (
              <button
                key={filter.key}
                onClick={() => setSelectedFilter(filter.key as any)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedFilter === filter.key
                    ? 'bg-naia-pink text-pearl-white'
                    : 'bg-dark-void/50 text-slate-grey hover:text-pearl-white'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Activity List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activityData.activities.length === 0 ? (
          <div className="text-center py-8">
            <MusicNoteIcon className="text-slate-grey text-4xl mb-2" />
            <p className="text-slate-grey">No recent activity</p>
            <p className="text-slate-grey text-sm">Start listening to see your activity here!</p>
          </div>
        ) : (
          activityData.activities.map((item) => (
            <div
              key={item.id}
              className={`flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-dark-void/30 ${
                compact ? 'py-2' : ''
              }`}
            >
              {/* Activity Icon */}
              <div className="flex-shrink-0 mt-1">
                {getActivityIcon(item)}
              </div>

              {/* Activity Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className={`text-slate-grey ${compact ? 'text-sm' : ''}`}>
                      {getActivityText(item)}
                    </p>
                    
                    {/* Artist/Album info for songs */}
                    {item.song && (
                      <p className="text-slate-grey text-sm mt-1">
                        by {item.song.artist_name}
                        {item.song.album_name && ` • ${item.song.album_name}`}
                      </p>
                    )}
                  </div>

                  {/* Play button for songs */}
                  {showPlayButton && item.song && (
                    <button
                      onClick={() => handlePlaySong(item.song!.id)}
                      className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-naia-pink/20 transition-colors"
                      title="Play this song"
                    >
                      <PlayArrowIcon className="text-naia-pink text-sm" />
                    </button>
                  )}
                </div>

                {/* Timestamp */}
                <p className="text-slate-grey text-xs mt-2">
                  {formatTimeAgo(item.timestamp)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Load More / Stats */}
      {activityData.hasMore && !compact && (
        <div className="mt-4 text-center">
          <button
            onClick={() => refetch()}
            className="text-naia-pink hover:text-soft-violet transition-colors text-sm"
          >
            View more activities
          </button>
        </div>
      )}
      
      {!compact && activityData.totalCount > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-slate-grey text-xs text-center">
            Showing {activityData.activities.length} of {activityData.totalCount} activities
          </p>
        </div>
      )}
    </Card>
  );
} 